import {
  Component,
  computed,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe, NgOptimizedImage } from '@angular/common';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@app/pipes/translate.pipe';

import { PriceGuide } from '@models/guides/priceGuide.model';
import { Vertical } from '@models/vertical/vertical.model';

import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

import { CardLoaderComponent } from '@components/shared/loaders/card-loader/card-loader.component';
import { GenericCardComponent } from '@components/shared/generic-card/generic-card.component';
import { indexPagePath } from '@helpers/indexPagePath';
import { HeaderLoaderComponent } from '@components/shared/loaders/header-loader/header-loader.component';
import { ReviewOverviewComponent } from '@components/reviewOverview/reviewOverview.component';
import { PriceAverageComponent } from '@app/pages/guides/priceAverage/priceAverage.component';
import { FormatCurrencyRangePipe } from '@app/pipes/format-currency-range.pipe';

type EntityDataItem = PriceGuide | Vertical | any;
@Component({
  imports: [
    FormatCurrencyRangePipe,
    CommonModule,
    TranslatePipe,
    DatePipe,
    NgOptimizedImage,
    CardLoaderComponent,
    HeaderLoaderComponent,
    RouterModule,
    MatIconModule,
    ReviewOverviewComponent,
    PriceAverageComponent,
  ],
  selector: 'app-custom-card',
  templateUrl: './custom-card.component.html',
  styleUrls: [
    '../generic-card/generic-card.component.scss',
    './custom-card.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { ngSkipHydration: 'true' },
})
export class CustomCardComponent extends GenericCardComponent {
  public readonly categoryOrVerticalName = input<string>();
  public readonly verticalSlug = input<string>();
  public readonly reviewCount = input<any>();
  public readonly priceGuideId = input<number>();
  public readonly isVerticalCard = input<boolean>();
  public readonly isAiPriceGuide = input<boolean>(false);
  public readonly shouldDisplayReviewComp = input<boolean>(false);
  public readonly shouldDisplayPriceComp = input<boolean>(false);

  protected readonly todayDate = new Date();
  protected readonly defaultImage = '/assets/svg/logo.svg';
  protected readonly aiPriceGuideIndexLink = computed(() =>
    indexPagePath(this.seoSegments.indexPage.aiPriceGuide, [
      this.headCategory(),
      this.subCategory(),
      this.subSubCategory(),
    ]),
  );

  protected isPriceGuide(entity: EntityDataItem): boolean {
    return entity && entity.price_guide !== undefined;
  }

  protected readonly Number = Number;

  protected getDate(
    dateInput: string | number | null | undefined,
  ): Date | null {
    if (!dateInput) {
      return null;
    }

    let date: Date;

    // If input is number or numeric string treat as timestamp
    if (
      typeof dateInput === 'number' ||
      (!isNaN(Number(dateInput)) && typeof dateInput === 'string')
    ) {
      date = new Date(Number(dateInput));
    } else {
      date = new Date(dateInput);
    }

    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Provides a stable identifier for Angular's trackBy functionality
   * This ensures consistent rendering between server and client
   */
  protected override getTrackById(item: any): any {
    if (!item) {
      return Math.random();
    }
    if (item.id) {
      return typeof item.id === 'number' ? `id_${item.id}` : item.id;
    }
    if (item._id) {
      return typeof item._id === 'number' ? `_id_${item._id}` : item._id;
    }
    if (item.uniqueId) {
      return item.uniqueId;
    }

    // Fall back
    if (item.title) {
      return `title_${item.title}`;
    }

    try {
      return JSON.stringify({
        title: item.title || '',
        description: item.description || item.meta_description || '',
      });
    } catch (e) {}
  }

  /**
   * Safely gets URL for an entity, handling potential null values
   */
  protected override generateUrl(entity: EntityDataItem): string {
    if (!entity) {
      return '';
    }
    try {
      return super.generateUrl(entity);
    } catch (error) {
      return '';
    }
  }
}
